import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { DriveSweepModel } from '../../../../../types';
import {
  UserKeys,
  createDriveMonitor,
  getDriveRootId,
} from '../../../../api/users-api';

import { useStore } from '../../../state/zustand-store';
import { handleErrors } from '../../../../utils/errors';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { Messages } from '../../../../utils/message-constants';
import SimpleFormPage from '../../../../shared/simple-form-page';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { pagesConfig } from '../../../../shared/pages-config';
import UnsavedChangesDialog, {
  useUnsavedChanges,
} from '../../../../shared/unsaved-changes-dialog';
import { useNavigate } from 'react-router-dom';
import SimpleBreadcrumbsPage from '../../../../shared/simple-breadcrumbs-page';
import ConfigurationStep from './configuration-step';
import SharedFormStepper, {
  StepConfig,
} from '../../../../shared/shared-form-stepper';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
const DriveSweepBaseSchema = z.object({
  name: z.string().min(1, 'Name is required.'),
  desc: z.string().optional(),
  ownerId: z.string(),
  folderId: z.string(),
  isMyDrive: z.boolean(),
  primaryDomain: z.string(),
  secondaryDomains: z.array(z.string()),
  sendShareNotifications: z.boolean(),
  lastRun: z.number(),
  created: z.number(),
  lastUpdated: z.number(),
  createdBy: z.string(),
  updatedBy: z.string(),
  folderName: z.string(),
});

export type DriveSweepFormValues = z.infer<typeof DriveSweepBaseSchema>;

export default function CreateDriveSweepFormPage() {
  const { changeLogRoles, loggedInUser } = useUserSettings();
  const navigate = useNavigate();
  const [stepCompleted, setStepCompleted] = useState<boolean[]>([false, false]);

  const [activeStep, setActiveStep] = useState(0);
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const DriveSweepSchema = useMemo(
    () =>
      DriveSweepBaseSchema.superRefine((data, ctx) => {
        const allSweeps =
          queryClient.getQueryData<DriveSweepModel[]>([
            UserKeys.DriveSweepModels,
          ]) ?? [];

        const isFolderInUse = allSweeps.some(
          (sweep) => sweep.folderId === data.folderId
        );

        if (isFolderInUse) {
          ctx.addIssue({
            path: ['folderId'],
            message: 'Drive Sweep already created for this folder.',
            code: 'custom',
          });
        }
      }),
    [queryClient]
  );

  const steps: StepConfig[] = [
    {
      label: 'Configuration',
      schema: DriveSweepSchema,
    },
  ];

  const [autoCompleteValue, setAutoCompleteValue] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('');
  const [selectedFolderOwnedByMe, setSelectedFolderOwnedByMe] = useState('');

  const { data: driveId, isLoading: driveIdLoading } = useQuery({
    queryKey: [UserKeys.DriveRootId, autoCompleteValue],
    queryFn: () => getDriveRootId(autoCompleteValue),
    enabled: autoCompleteValue !== '',
    meta: {
      errorMessage: Messages.Errors.Network.Loading('drive root id'),
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValidating, isSubmitting, isDirty },
    trigger,
    reset,
    getValues,
    setValue,
    watch,
  } = useForm<DriveSweepFormValues>({
    resolver: zodResolver(DriveSweepSchema),
    defaultValues: {
      folderId: '',
      ownerId: '',
      primaryDomain: '',
      secondaryDomains: [],
      name: '',
      desc: '',
      isMyDrive: true,
      sendShareNotifications: false,
      lastRun: 0,
      created: 0,
      lastUpdated: 0,
      createdBy: '',
      updatedBy: '',
      folderName: '',
    },
  });

  const handleCancel = () => {
    navigate(`/${pagesConfig.driveSweep.link}`, {
      state: { fromCancelButton: true },
    });
  };

  const onSubmit: SubmitHandler<Partial<DriveSweepModel>> = (data) => {
    if (
      isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanCreateDriveSweep.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      const newSweep: Partial<DriveSweepModel> = {
        ...data,
      };
      mutate(newSweep);
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (data: Partial<DriveSweepModel>) =>
      createDriveMonitor(data, changeLogMessage),
    onSuccess: (_data, newSweep: Partial<DriveSweepModel>) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Added('Drive sweep'),
      });

      queryClient.setQueryData(
        [UserKeys.DriveSweepModels],
        (oldData: DriveSweepModel[] = []) => [
          ...oldData,
          {
            ...newSweep,
            isRunning: true,
            lastRun: Date.now(),
            created: Date.now(),
            createdBy: loggedInUser,
          },
        ]
      );

      setChangeLogMessage('');
      setActiveStep(0);
      reset();
      bypass();
      navigate(`/${pagesConfig.driveSweep.link}`);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Adding('drive sweep'),
      });
    },
    onSettled: () => {
      createEvent(analytics.drive.drive_sweep.create_drive_sweep, {
        module: ModuleList.modules.drive.name,
        section: ModuleList.modules.drive.sections.driveSweep,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const {
    showDialog: showUnsavedChangesDialog,
    handleConfirm: handleUnsavedConfirm,
    handleCancel: handleUnsavedCancel,
    bypass,
    isCancelAction,
  } = useUnsavedChanges(isDirty);

  const formStepperProps = {
    activeStep,
    setActiveStep,
    steps,
    trigger,
    errors,
    stepCompleted,
    setStepCompleted,
  };
  return (
    <SimpleBreadcrumbsPage
      isSubPage
      breadcrumbs={[
        { label: ModuleList.modules.automation.name },
        {
          label: pagesConfig.driveSweep.name,
          path: pagesConfig.driveSweep.link,
        },
      ]}
    >
      <SimpleFormPage
        title={pagesConfig.createDriveSweepFormPage.name}
        stepper={
          <SharedFormStepper<DriveSweepFormValues> {...formStepperProps} />
        }
        {...formStepperProps}
        content={
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {steps[activeStep].label === 'Configuration' && (
              <ConfigurationStep
                control={control}
                errors={errors}
                setValue={setValue}
                watch={watch}
                driveId={driveId}
                autoCompleteValue={autoCompleteValue}
                setAutoCompleteValue={setAutoCompleteValue}
                driveIdLoading={driveIdLoading}
                selectedFolder={selectedFolder}
                setSelectedFolder={setSelectedFolder}
                selectedFolderOwnedByMe={selectedFolderOwnedByMe}
                setSelectedFolderOwnedByMe={setSelectedFolderOwnedByMe}
              />
            )}
          </Box>
        }
        handleDiscard={handleCancel}
        handleSubmit={handleSubmit(onSubmit)}
        submitButtonText={pagesConfig.createDriveSweepFormPage.name}
        isLoadingSubmit={isValidating || isSubmitting || isPending}
        isDisableSubmit={
          (activeStep === steps.length - 1 && watch('ownerId') === '') ||
          (activeStep === steps.length - 1 && driveId === selectedFolder) ||
          (activeStep === steps.length - 1 &&
            selectedFolderOwnedByMe === 'ownedByMe')
        }
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          const newTemplate: Partial<DriveSweepModel> = {
            ...getValues(),
          };
          mutate(newTemplate);
        }}
        mutateLoading={isPending}
      />
      <UnsavedChangesDialog
        isCancelAction={isCancelAction}
        entityName="Drive Sweep"
        pageMode="create"
        open={showUnsavedChangesDialog}
        handleConfirm={handleUnsavedConfirm}
        handleClose={handleUnsavedCancel}
      />
    </SimpleBreadcrumbsPage>
  );
}
